import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

export type DeviceAssociationOption = 'associated' | 'not_associated';

const ASSOCIATION_OPTIONS: DeviceAssociationOption[] = ['associated', 'not_associated'];

export interface MultiSelectDeviceAssociationProps {
  size?: 'sm' | 'md';
  selectedAssociation: DeviceAssociationOption[];
  onChange: (association: DeviceAssociationOption[]) => void;
}

const MultiSelectDeviceAssociation = ({ size = 'md', selectedAssociation, onChange }: MultiSelectDeviceAssociationProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState<DeviceAssociationOption[]>(selectedAssociation || []);

  const hasNotChanged = selectedAssociation?.length === draft?.length && selectedAssociation.every((value) => draft?.includes(value));

  const onClick = useCallback(() => {
    setDraft(selectedAssociation || []);
  }, [selectedAssociation]);

  const handleClear = useCallback(() => {
    setDraft([]);
  }, []);

  const handleClose = useCallback(() => {
    setForceClosed(true);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (association: DeviceAssociationOption) => {
      const existItem = draft.includes(association);
      const newDraft = existItem ? draft.filter((value) => value !== association) : [...draft, association];
      setDraft(newDraft);
    },
    [draft]
  );

  const getAssociationLabel = (association: DeviceAssociationOption) => {
    switch (association) {
      case 'associated':
        return t('device_association.associated');
      case 'not_associated':
        return t('device_association.not_associated');
    }
  };

  return (
    <Dropdown
      onOpenChange={onClick}
      forceClose={forceClosed}
      portal={false}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={handleClear}>
              {t('device_association.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-4'>
            <Dropdown.List
              heightItem={30}
              data={ASSOCIATION_OPTIONS}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: DeviceAssociationOption) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={`association-${item}`} key={item} checked={draft.includes(item)} onCheckedChange={() => toggleItem(item)} />
                  <Label htmlFor={`association-${item}`}>{getAssociationLabel(item)}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button variant='ghost' full size='sm' onClick={handleClose}>
                {t('device_association.cancel')}
              </Button>
              <Button size='sm' full onClick={handleApply} disabled={hasNotChanged}>
                {t('device_association.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='link' />}>
        {t('device_association.title')} {(selectedAssociation?.length || 0) > 0 ? `(${selectedAssociation?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectDeviceAssociation;
